import { useState, useRef, useEffect, useCallback } from 'react';
import {
  Fab, Paper, Box, Typography, IconButton,
  Avatar, Chip, CircularProgress, Divider,
  Zoom, TextField, Tooltip, LinearProgress,
  Table, TableBody, TableCell, TableHead, TableRow,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import PersonIcon from '@mui/icons-material/Person';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PaymentsIcon from '@mui/icons-material/Payments';
import CardMembershipIcon from '@mui/icons-material/CardMembership';
import PhoneIcon from '@mui/icons-material/Phone';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import TodayIcon from '@mui/icons-material/Today';
import DateRangeIcon from '@mui/icons-material/DateRange';
import CurrencyRupeeIcon from '@mui/icons-material/CurrencyRupee';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import HistoryIcon from '@mui/icons-material/History';
import AssessmentIcon from '@mui/icons-material/Assessment';
import GroupIcon from '@mui/icons-material/Group';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import BadgeIcon from '@mui/icons-material/Badge';
import {
  AutocompleteMember,
  retrieveGymMember,
  retrievePaymentHistory,
} from '../../pages/Apihelper/MemberApiHelpr';
import {
  DashboardCount,
  DashboardBarChart,
  getUpcomingExpire,
  getPendingDue,
  getActivePT,
} from '../../pages/Apihelper/Dashboard';
import { useTheme as useAppTheme } from '../../contexts/ThemeContext';

// ─── helpers ──────────────────────────────────────────────────────────────────

function getAuth() {
  const s = localStorage.getItem('userdata');
  if (!s) return null;
  const o = JSON.parse(s);
  return { token: o.token, userId: o.id };
}

function getLoginUserName() {
  try {
    const o = JSON.parse(localStorage.getItem('userdata') || '{}');
    return o.name || o.user_name || o.username || o.email || '';
  } catch { return ''; }
}

function parseDate(str) {
  if (!str) return null;
  const p = str.split('-');
  if (p.length !== 3) return null;
  const d = p[0].length === 4
    ? new Date(`${p[0]}-${p[1]}-${p[2]}`)
    : new Date(`${p[2]}-${p[1]}-${p[0]}`);
  return isNaN(d) ? null : d;
}

function getRemainingDays(str) {
  const d = parseDate(str);
  if (!d) return null;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  d.setHours(0, 0, 0, 0);
  return Math.round((d - today) / 86400000);
}

function fmtDate(str) {
  const d = parseDate(str);
  return d ? d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : str;
}

function fmtNum(n) {
  return Number(n || 0).toLocaleString('en-IN');
}

// ─── intent detection ─────────────────────────────────────────────────────────

const INCOME_RE = /\b(income|collection|revenue|earning)/i;
const TODAY_RE = /\b(today|today's)/i;
const MONTH_RE = /\b(month|monthly)/i;
const HISTORY_RE = /\b(payment\s+history|history|paid|invoice|transaction|receipt)/i;

function detectIntent(text) {
  const t = text.toLowerCase();
  if (INCOME_RE.test(t)) return TODAY_RE.test(t) ? 'income_today' : MONTH_RE.test(t) ? 'income_month' : 'income_today';
  if (/\b(summary|overview|analyz|analytic|report|gym\s+status|gym\s+health|gym\s+data|dashboard)\b/.test(t)) return 'gym_summary';
  if (/\b(expir|upcoming\s+expir|renewal|renew\s+soon|near\s+expir|about\s+to\s+expir)/.test(t) && !/payment/.test(t)) return 'expiring_soon';
  if (/\b(due\s+member|pending\s+due|who\s+owe|overdue|outstanding|due\s+list|member.*due|due.*amount)/.test(t) && !/payment\s+history/.test(t)) return 'due_members';
  if (/\b(active\s*pt|pt\s*member|personal\s*train|trainer\s*member|active\s*trainer)\b/.test(t)) return 'active_pt';
  if (/\b(monthly\s*trend|trend|revenue\s*trend|payment\s*trend|monthly\s*chart)\b/.test(t)) return 'monthly_trend';
  if (HISTORY_RE.test(t)) return 'payment_history';
  return 'member_search';
}

// ─── Streaming text ───────────────────────────────────────────────────────────

function StreamText({ text, speed = 8 }) {
  const [len, setLen] = useState(0);
  const ref = useRef(null);

  useEffect(() => {
    setLen(0);
    let i = 0;
    const tick = () => {
      i = Math.min(i + 4, text.length);
      setLen(i);
      if (i < text.length) ref.current = setTimeout(tick, speed);
    };
    ref.current = setTimeout(tick, speed);
    return () => clearTimeout(ref.current);
  }, [text, speed]);

  return <>{text.slice(0, len)}</>;
}

// ─── Typing indicator ─────────────────────────────────────────────────────────

function TypingDots() {
  return (
    <Box sx={{ display: 'flex', gap: '5px', alignItems: 'center', py: 0.5 }}>
      {[0, 1, 2].map((i) => (
        <Box key={i} sx={{
          width: 7, height: 7, borderRadius: '50%',
          bgcolor: 'primary.main', opacity: 0.7,
          animation: 'bounce 1.2s infinite ease-in-out',
          animationDelay: `${i * 0.2}s`,
          '@keyframes bounce': {
            '0%, 80%, 100%': { transform: 'translateY(0)' },
            '40%': { transform: 'translateY(-6px)' },
          },
        }} />
      ))}
    </Box>
  );
}

// ─── Bot / User bubbles ───────────────────────────────────────────────────────

function BotBubble({ text, extra, children, stream, noPad }) {
  const { colors: appColors } = useAppTheme();
  return (
    <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', mb: 1.5 }}>
      <Avatar sx={{ width: 30, height: 30, bgcolor: appColors.evergreen, flexShrink: 0, mt: 0.25 }}>
        <SmartToyIcon sx={{ fontSize: 16 }} />
      </Avatar>
      <Box sx={{ maxWidth: '90%', width: '100%' }}>
        {!noPad && text && (
          <Paper elevation={1} sx={{
            p: 1.25, px: 1.75, borderRadius: '4px 14px 14px 14px',
            bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider',
          }}>
            <Typography variant="body2" sx={{ lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
              {stream ? <StreamText text={text} /> : text}
            </Typography>
          </Paper>
        )}
        {extra}
        {noPad && children}
      </Box>
    </Box>
  );
}

function UserBubble({ text }) {
  const { colors: appColors } = useAppTheme();
  return (
    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1.5 }}>
      <Box sx={{
        px: 2, py: 1, borderRadius: '14px 4px 14px 14px',
        background: `linear-gradient(135deg,${appColors.leaf},#1e88e5)`,
        color: 'white', maxWidth: '75%',
      }}>
        <Typography variant="body2" sx={{ lineHeight: 1.5 }}>{text}</Typography>
      </Box>
    </Box>
  );
}

// ─── InfoRow ──────────────────────────────────────────────────────────────────

function InfoRow({ icon, value, color, bold }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <Box sx={{ color: color || 'text.secondary', display: 'flex', flexShrink: 0, '& svg': { fontSize: 12 } }}>{icon}</Box>
      <Typography variant="caption" color={color || 'text.secondary'} fontWeight={bold ? 600 : 400} noWrap title={value}>
        {value}
      </Typography>
    </Box>
  );
}

// ─── Member card ──────────────────────────────────────────────────────────────

function MemberCard({ member, onFollowUp }) {
  const { colors: appColors } = useAppTheme();
  const lp = member.lastPayment;
  const endDate = member.end_date || lp?.end_date || null;
  const due = member.due != null && member.due !== '' ? member.due : lp?.due ?? null;
  const pkg = member.package_name || lp?.payment_type || null;
  const rem = getRemainingDays(endDate);
  const expired = rem !== null && rem < 0;
  const warn = rem !== null && rem >= 0 && rem <= 30;
  const dayColor = expired ? '#ef4444' : warn ? '#f59e0b' : '#10b981';

  return (
    <Box sx={{
      mt: 0.75, p: 1.5, borderRadius: 2,
      bgcolor: 'background.paper',
      border: '1px solid', borderColor: 'divider',
      borderLeft: `4px solid ${dayColor}`,
      boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, flexWrap: 'wrap', gap: 0.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Avatar sx={{ width: 26, height: 26, bgcolor: '#e3f2fd', color: appColors.leaf, fontSize: 13 }}>
            <PersonIcon sx={{ fontSize: 15 }} />
          </Avatar>
          <Typography variant="subtitle2" fontWeight={700} fontSize={13}>{member.name}</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          {member.membership_number && (
            <Chip label={`#${member.membership_number}`} size="small" variant="outlined" sx={{ fontSize: 10, height: 18, borderRadius: 1 }} />
          )}
          <Chip
            label={member.status === 1 ? 'Active' : 'Inactive'} size="small"
            sx={{ fontSize: 10, height: 18, borderRadius: 1, bgcolor: member.status === 1 ? '#dcfce7' : '#fee2e2', color: member.status === 1 ? '#16a34a' : '#dc2626', border: 'none' }}
          />
        </Box>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.5 }}>
        {member.phone && <InfoRow icon={<PhoneIcon />} value={member.phone} />}
        {pkg && <InfoRow icon={<CardMembershipIcon />} value={pkg} />}
        {endDate && (
          <InfoRow icon={<CalendarTodayIcon />} value={`${expired ? 'Expired' : 'Valid till'}: ${fmtDate(endDate)}`} color={dayColor} bold />
        )}
        {rem !== null && (
          <InfoRow icon={<AccessTimeIcon />}
            value={expired ? `Expired ${Math.abs(rem)}d ago` : rem === 0 ? 'Expires today!' : `${rem} days left`}
            color={dayColor} bold />
        )}
        {due != null && Number(due) > 0 && (
          <InfoRow icon={<PaymentsIcon />} value={`Due: ₹${due}`} color="#f59e0b" bold />
        )}
        {lp?.amount && (
          <Box sx={{ gridColumn: 'span 2' }}>
            <InfoRow icon={<ReceiptLongIcon />} value={`Last paid: ₹${lp.amount}${lp.date ? ` (${fmtDate(lp.date)})` : ''}`} color="#6366f1" />
          </Box>
        )}
      </Box>

      {onFollowUp && (
        <Box sx={{ mt: 1, pt: 0.75, borderTop: '1px dashed', borderColor: 'divider', display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
          <Chip icon={<HistoryIcon sx={{ fontSize: 13 }} />} label="Payment History" size="small"
            onClick={() => onFollowUp('payment_history', member)}
            sx={{ fontSize: 10, height: 22, cursor: 'pointer', '&:hover': { bgcolor: 'primary.light', color: 'white' } }}
            variant="outlined" color="primary" />
          <Chip icon={<TodayIcon sx={{ fontSize: 13 }} />} label="Today's Income" size="small"
            onClick={() => onFollowUp('income_today', null)}
            sx={{ fontSize: 10, height: 22, cursor: 'pointer', '&:hover': { bgcolor: 'success.light', color: 'white' } }}
            variant="outlined" color="success" />
          <Chip icon={<AssessmentIcon sx={{ fontSize: 13 }} />} label="Gym Summary" size="small"
            onClick={() => onFollowUp('gym_summary', null)}
            sx={{ fontSize: 10, height: 22, cursor: 'pointer', '&:hover': { bgcolor: 'secondary.light', color: 'white' } }}
            variant="outlined" color="secondary" />
        </Box>
      )}
    </Box>
  );
}

// ─── Payment history card ─────────────────────────────────────────────────────

function PaymentHistoryCard({ memberName, payments }) {
  const { colors: appColors } = useAppTheme();
  return (
    <Box sx={{ mt: 0.75, borderRadius: 2, overflow: 'hidden', border: '1px solid', borderColor: 'divider', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
      <Box sx={{ px: 1.5, py: 1, bgcolor: appColors.evergreen, color: 'white', display: 'flex', alignItems: 'center', gap: 1 }}>
        <HistoryIcon sx={{ fontSize: 14 }} />
        <Typography variant="caption" fontWeight={700} letterSpacing={0.5}>
          PAYMENT HISTORY — {memberName?.toUpperCase()}
        </Typography>
      </Box>
      {payments.length === 0 ? (
        <Box sx={{ p: 2, textAlign: 'center' }}>
          <Typography variant="caption" color="text.secondary">No payment records found.</Typography>
        </Box>
      ) : (
        <Box sx={{ maxHeight: 200, overflowY: 'auto' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                {['Date', 'Amount', 'Due', 'Mode'].map(h => (
                  <TableCell key={h} sx={{ fontSize: 10, fontWeight: 700, py: 0.75, bgcolor: '#f8fafc', color: '#64748b' }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {payments.map((p, i) => (
                <TableRow key={i} sx={{ '&:nth-of-type(even)': { bgcolor: '#f8fafc' } }}>
                  <TableCell sx={{ fontSize: 10, py: 0.6 }}>{p.date ? fmtDate(p.date) : '—'}</TableCell>
                  <TableCell sx={{ fontSize: 10, py: 0.6, color: '#16a34a', fontWeight: 600 }}>₹{p.amount}</TableCell>
                  <TableCell sx={{ fontSize: 10, py: 0.6, color: Number(p.due) > 0 ? '#f59e0b' : '#64748b' }}>₹{p.due ?? 0}</TableCell>
                  <TableCell sx={{ fontSize: 10, py: 0.6, color: '#64748b' }}>{p.mode_of_payment || '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}
    </Box>
  );
}

// ─── Income card ──────────────────────────────────────────────────────────────

function IncomeCard({ type, data }) {
  const { colors: appColors } = useAppTheme();
  const isToday = type === 'income_today';
  const rows = isToday
    ? [
        { label: 'Main Package', value: data.today_main_pay },
        { label: 'PT Payments', value: data.today_trainer_pay },
      ]
    : [
        { label: 'Main Package', value: data.period_main_pay },
        { label: 'PT Payments', value: data.period_trainer_pay },
      ];
  const total = isToday ? data.today_total_payments : data.period_total_payments;

  return (
    <Box sx={{ mt: 0.75, borderRadius: 2, overflow: 'hidden', border: '1px solid', borderColor: 'divider', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
      <Box sx={{ px: 1.5, py: 1, bgcolor: appColors.evergreen, color: 'white', display: 'flex', alignItems: 'center', gap: 1 }}>
        <CurrencyRupeeIcon sx={{ fontSize: 14 }} />
        <Typography variant="caption" fontWeight={700} letterSpacing={0.5}>
          {isToday ? "TODAY'S INCOME" : "THIS MONTH'S INCOME"}
        </Typography>
      </Box>
      <Box sx={{ p: 1.25 }}>
        {rows.map(r => (
          <Box key={r.label} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.4 }}>
            <Typography variant="caption" color="text.secondary">{r.label}</Typography>
            <Typography variant="caption" fontWeight={500}>₹{fmtNum(r.value)}</Typography>
          </Box>
        ))}
        <Divider sx={{ my: 0.75 }} />
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="caption" fontWeight={700} color="primary.main">Total</Typography>
          <Typography variant="caption" fontWeight={700} color="primary.main">₹{fmtNum(total)}</Typography>
        </Box>
        {!isToday && data.total_due != null && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.25 }}>
            <Typography variant="caption" color="warning.main">Pending Due</Typography>
            <Typography variant="caption" color="warning.main" fontWeight={600}>₹{fmtNum(data.total_due)}</Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
}

// ─── Progress bar row ─────────────────────────────────────────────────────────

function BreakdownBar({ label, value, total, color }) {
  const pct = total > 0 ? Math.round((value / total) * 100) : 0;
  return (
    <Box sx={{ mb: 0.75 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.3 }}>
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>{label}</Typography>
        <Typography variant="caption" fontWeight={600} sx={{ fontSize: 10, color }}>₹{fmtNum(value)} ({pct}%)</Typography>
      </Box>
      <LinearProgress variant="determinate" value={pct} sx={{
        height: 5, borderRadius: 2, bgcolor: '#e2e8f0',
        '& .MuiLinearProgress-bar': { bgcolor: color, borderRadius: 2 },
      }} />
    </Box>
  );
}

// ─── Gym Summary card ─────────────────────────────────────────────────────────

function GymSummaryCard({ data }) {
  const { colors: appColors } = useAppTheme();
  const stats = [
    { label: 'Total Members', value: data.total_member ?? data.members ?? data.total_members ?? '—', icon: <GroupIcon />, color: appColors.leaf },
    { label: 'Active Members', value: data.active_member ?? data.active_members ?? '—', icon: <BadgeIcon />, color: '#10b981' },
    { label: "Today's Income", value: `₹${fmtNum(data.today_total_payments)}`, icon: <TodayIcon />, color: '#6366f1' },
    { label: 'Month Income', value: `₹${fmtNum(data.period_total_payments)}`, icon: <DateRangeIcon />, color: '#f59e0b' },
    { label: 'Total Due', value: `₹${fmtNum(data.total_due)}`, icon: <WarningAmberIcon />, color: '#ef4444' },
    { label: 'Employees', value: data.total_employees ?? data.employees ?? '—', icon: <FitnessCenterIcon />, color: '#8b5cf6' },
  ];

  const todayTotal = Number(data.today_total_payments || 0);

  return (
    <Box sx={{ mt: 0.75, borderRadius: 2, overflow: 'hidden', border: '1px solid', borderColor: 'divider', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
      <Box sx={{ px: 1.5, py: 1, background: `linear-gradient(135deg,${appColors.evergreen},${appColors.leaf})`, color: 'white', display: 'flex', alignItems: 'center', gap: 1 }}>
        <AssessmentIcon sx={{ fontSize: 14 }} />
        <Typography variant="caption" fontWeight={700} letterSpacing={0.5}>GYM ANALYTICS OVERVIEW</Typography>
      </Box>

      <Box sx={{ p: 1.25, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.75 }}>
        {stats.map(s => (
          <Box key={s.label} sx={{
            p: 0.9, borderRadius: 1.5, bgcolor: '#f8fafc',
            border: '1px solid', borderColor: 'divider',
            display: 'flex', alignItems: 'center', gap: 0.75,
          }}>
            <Box sx={{ color: s.color, display: 'flex', '& svg': { fontSize: 18 } }}>{s.icon}</Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: 9, display: 'block', lineHeight: 1.2 }}>{s.label}</Typography>
              <Typography fontWeight={700} sx={{ fontSize: 12, color: s.color, lineHeight: 1.4 }}>{s.value}</Typography>
            </Box>
          </Box>
        ))}
      </Box>

      <Box sx={{ px: 1.25, pb: 1.25 }}>
        <Divider sx={{ mb: 0.75 }} />
        <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ fontSize: 9, letterSpacing: 0.5, textTransform: 'uppercase' }}>Today's Breakdown</Typography>
        <Box sx={{ mt: 0.5 }}>
          <BreakdownBar label="Main Package" value={Number(data.today_main_pay || 0)} total={todayTotal || 1} color={appColors.leaf} />
          <BreakdownBar label="PT Payments" value={Number(data.today_trainer_pay || 0)} total={todayTotal || 1} color="#10b981" />
        </Box>
      </Box>
    </Box>
  );
}

// ─── Expiring Soon card ───────────────────────────────────────────────────────

function ExpiringMembersCard({ members }) {
  return (
    <Box sx={{ mt: 0.75, borderRadius: 2, overflow: 'hidden', border: '1px solid', borderColor: 'divider', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
      <Box sx={{ px: 1.5, py: 1, bgcolor: '#b45309', color: 'white', display: 'flex', alignItems: 'center', gap: 1 }}>
        <WarningAmberIcon sx={{ fontSize: 14 }} />
        <Typography variant="caption" fontWeight={700} letterSpacing={0.5}>
          EXPIRING SOON ({members.length})
        </Typography>
      </Box>
      {members.length === 0 ? (
        <Box sx={{ p: 2, textAlign: 'center' }}>
          <Typography variant="caption" color="text.secondary">No members expiring soon.</Typography>
        </Box>
      ) : (
        <Box sx={{ maxHeight: 220, overflowY: 'auto' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                {['Member', 'End Date', 'Left'].map(h => (
                  <TableCell key={h} sx={{ fontSize: 10, fontWeight: 700, py: 0.75, bgcolor: '#fffbeb', color: '#92400e' }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {members.slice(0, 20).map((m, i) => {
                const rem = getRemainingDays(m.end_date);
                const urgent = rem !== null && rem <= 7;
                return (
                  <TableRow key={i} sx={{ '&:nth-of-type(even)': { bgcolor: '#fffbeb' } }}>
                    <TableCell sx={{ py: 0.6 }}>
                      <Typography sx={{ fontSize: 10, fontWeight: 600 }}>{m.name || m.member_name}</Typography>
                      {m.membership_number && (
                        <Typography sx={{ fontSize: 9, color: '#92400e' }}>#{m.membership_number}</Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ fontSize: 10, py: 0.6, color: '#64748b' }}>{m.end_date ? fmtDate(m.end_date) : '—'}</TableCell>
                    <TableCell sx={{ py: 0.6 }}>
                      <Chip
                        label={rem !== null ? `${rem}d` : '—'} size="small"
                        sx={{ fontSize: 9, height: 17, bgcolor: urgent ? '#fee2e2' : '#fef9c3', color: urgent ? '#dc2626' : '#92400e', border: 'none', fontWeight: 700 }}
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>
      )}
    </Box>
  );
}

// ─── Due Members card ─────────────────────────────────────────────────────────

function DueMembersCard({ members }) {
  return (
    <Box sx={{ mt: 0.75, borderRadius: 2, overflow: 'hidden', border: '1px solid', borderColor: 'divider', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
      <Box sx={{ px: 1.5, py: 1, bgcolor: '#b91c1c', color: 'white', display: 'flex', alignItems: 'center', gap: 1 }}>
        <PaymentsIcon sx={{ fontSize: 14 }} />
        <Typography variant="caption" fontWeight={700} letterSpacing={0.5}>
          PENDING DUES ({members.length})
        </Typography>
      </Box>
      {members.length === 0 ? (
        <Box sx={{ p: 2, textAlign: 'center' }}>
          <Typography variant="caption" color="text.secondary">No pending dues found.</Typography>
        </Box>
      ) : (
        <Box sx={{ maxHeight: 220, overflowY: 'auto' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                {['Member', 'Phone', 'Due'].map(h => (
                  <TableCell key={h} sx={{ fontSize: 10, fontWeight: 700, py: 0.75, bgcolor: '#fef2f2', color: '#7f1d1d' }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {members.slice(0, 20).map((m, i) => (
                <TableRow key={i} sx={{ '&:nth-of-type(even)': { bgcolor: '#fef2f2' } }}>
                  <TableCell sx={{ py: 0.6 }}>
                    <Typography sx={{ fontSize: 10, fontWeight: 600 }}>{m.name || m.member_name}</Typography>
                    {m.membership_number && (
                      <Typography sx={{ fontSize: 9, color: '#7f1d1d' }}>#{m.membership_number}</Typography>
                    )}
                  </TableCell>
                  <TableCell sx={{ fontSize: 10, py: 0.6, color: '#64748b' }}>{m.phone || '—'}</TableCell>
                  <TableCell sx={{ py: 0.6 }}>
                    <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#ef4444' }}>
                      ₹{fmtNum(m.due || m.total_due || m.due_amount || 0)}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}
    </Box>
  );
}

// ─── Active PT card ───────────────────────────────────────────────────────────

function ActivePTCard({ members }) {
  return (
    <Box sx={{ mt: 0.75, borderRadius: 2, overflow: 'hidden', border: '1px solid', borderColor: 'divider', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
      <Box sx={{ px: 1.5, py: 1, bgcolor: '#065f46', color: 'white', display: 'flex', alignItems: 'center', gap: 1 }}>
        <FitnessCenterIcon sx={{ fontSize: 14 }} />
        <Typography variant="caption" fontWeight={700} letterSpacing={0.5}>
          ACTIVE PT MEMBERS ({members.length})
        </Typography>
      </Box>
      {members.length === 0 ? (
        <Box sx={{ p: 2, textAlign: 'center' }}>
          <Typography variant="caption" color="text.secondary">No active PT members found.</Typography>
        </Box>
      ) : (
        <Box sx={{ maxHeight: 220, overflowY: 'auto' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                {['Member', 'Trainer', 'Expires'].map(h => (
                  <TableCell key={h} sx={{ fontSize: 10, fontWeight: 700, py: 0.75, bgcolor: '#f0fdf4', color: '#14532d' }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {members.slice(0, 20).map((m, i) => (
                <TableRow key={i} sx={{ '&:nth-of-type(even)': { bgcolor: '#f0fdf4' } }}>
                  <TableCell sx={{ py: 0.6 }}>
                    <Typography sx={{ fontSize: 10, fontWeight: 600 }}>{m.name || m.member_name}</Typography>
                    {m.phone && <Typography sx={{ fontSize: 9, color: '#14532d' }}>{m.phone}</Typography>}
                  </TableCell>
                  <TableCell sx={{ fontSize: 10, py: 0.6, color: '#065f46', fontWeight: 500 }}>
                    {m.trainer_name || m.employee_name || m.trainer || '—'}
                  </TableCell>
                  <TableCell sx={{ fontSize: 10, py: 0.6, color: '#64748b' }}>
                    {(m.expire_date || m.end_date) ? fmtDate(m.expire_date || m.end_date) : '—'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}
    </Box>
  );
}

// ─── Monthly Trend card ───────────────────────────────────────────────────────

function MonthlyTrendCard({ data }) {
  const rows = Array.isArray(data) ? data : (data?.data || data?.monthly || data?.summary || []);
  const max = rows.length ? Math.max(...rows.map(r => Number(r.total || r.amount || r.total_payment || r.total_payments || 0))) : 0;

  return (
    <Box sx={{ mt: 0.75, borderRadius: 2, overflow: 'hidden', border: '1px solid', borderColor: 'divider', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
      <Box sx={{ px: 1.5, py: 1, bgcolor: '#4338ca', color: 'white', display: 'flex', alignItems: 'center', gap: 1 }}>
        <TrendingUpIcon sx={{ fontSize: 14 }} />
        <Typography variant="caption" fontWeight={700} letterSpacing={0.5}>MONTHLY REVENUE TREND</Typography>
      </Box>
      {rows.length === 0 ? (
        <Box sx={{ p: 2, textAlign: 'center' }}>
          <Typography variant="caption" color="text.secondary">No monthly trend data available.</Typography>
        </Box>
      ) : (
        <Box sx={{ p: 1.25 }}>
          {rows.slice(-6).map((r, i) => {
            const val = Number(r.total || r.amount || r.total_payment || r.total_payments || 0);
            const pct = max > 0 ? Math.round((val / max) * 100) : 0;
            const label = r.month || r.month_name || r.label || r.period || `Period ${i + 1}`;
            return (
              <Box key={i} sx={{ mb: 0.75 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.3 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>{label}</Typography>
                  <Typography variant="caption" fontWeight={700} sx={{ fontSize: 10, color: '#4338ca' }}>₹{fmtNum(val)}</Typography>
                </Box>
                <LinearProgress variant="determinate" value={pct} sx={{
                  height: 6, borderRadius: 2, bgcolor: '#e0e7ff',
                  '& .MuiLinearProgress-bar': { bgcolor: '#4338ca', borderRadius: 2 },
                }} />
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
}

// ─── Quick-action chips ───────────────────────────────────────────────────────

function QuickChips({ onAction }) {
  const chips = [
    { label: "Today's Income", icon: <TodayIcon />, action: 'income_today', color: 'success' },
    { label: 'This Month', icon: <DateRangeIcon />, action: 'income_month', color: 'primary' },
    { label: 'Gym Summary', icon: <AssessmentIcon />, action: 'gym_summary', color: 'secondary' },
    { label: 'Expiring Soon', icon: <WarningAmberIcon />, action: 'expiring_soon', color: 'warning' },
    { label: 'Due Members', icon: <PaymentsIcon />, action: 'due_members', color: 'error' },
    { label: 'Active PT', icon: <FitnessCenterIcon />, action: 'active_pt', color: 'success' },
    { label: 'Monthly Trend', icon: <TrendingUpIcon />, action: 'monthly_trend', color: 'primary' },
  ];
  return (
    <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mt: 0.75 }}>
      {chips.map(c => (
        <Chip key={c.action} icon={c.icon} label={c.label} size="small" color={c.color}
          variant="outlined" onClick={() => onAction(c.action)}
          sx={{ fontSize: 10, height: 24, cursor: 'pointer' }}
        />
      ))}
    </Box>
  );
}

// ─── Main ChatBot ─────────────────────────────────────────────────────────────

const QUICK_ACTION_MAP = {
  income_today: "Today's income",
  income_month: "This month's income",
  gym_summary: 'Gym summary',
  expiring_soon: 'Expiring soon members',
  due_members: 'Due members',
  active_pt: 'Active PT members',
  monthly_trend: 'Monthly revenue trend',
};

function makeWelcome() {
  const name = getLoginUserName();
  const greeting = name ? `Hi, ${name}! 👋` : 'Hi there! 👋';
  return {
    type: 'bot', stream: true,
    text: `${greeting} I'm your Gym Assistant 💪\n\nType a member name to look them up, or tap a quick action below — they're always available at the bottom.`,
    id: 'welcome',
  };
}

export default function ChatBot() {
  const { colors: appColors } = useAppTheme();
  const [open, setOpen] = useState(false);

  // ── Draggable launcher: the button can be dropped anywhere and remembers where ──
  const FAB_SIZE = 56;
  const EDGE = 8;
  const POS_KEY = 'gymAssistantPos';
  const [vp, setVp] = useState({ w: window.innerWidth, h: window.innerHeight });
  const clampPos = (p, v = vp) => ({
    left: Math.max(EDGE, Math.min(v.w - FAB_SIZE - EDGE, p.left)),
    top: Math.max(EDGE, Math.min(v.h - FAB_SIZE - EDGE, p.top)),
  });
  const [pos, setPos] = useState(() => {
    const v = { w: window.innerWidth, h: window.innerHeight };
    try {
      const saved = JSON.parse(localStorage.getItem(POS_KEY) || 'null');
      if (saved && Number.isFinite(saved.left) && Number.isFinite(saved.top)) return clampPos(saved, v);
    } catch (e) { /* use the default corner */ }
    return clampPos({ left: v.w - FAB_SIZE - 24, top: v.h - FAB_SIZE - 24 }, v);
  });
  const dragRef = useRef({ active: false, moved: false, startX: 0, startY: 0, left: 0, top: 0 });

  useEffect(() => {
    const onResize = () => {
      const v = { w: window.innerWidth, h: window.innerHeight };
      setVp(v);
      setPos((p) => clampPos(p, v));
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []); // eslint-disable-line

  const onFabPointerDown = (e) => {
    dragRef.current = { active: true, moved: false, startX: e.clientX, startY: e.clientY, left: pos.left, top: pos.top };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  const onFabPointerMove = (e) => {
    const d = dragRef.current;
    if (!d.active) return;
    const dx = e.clientX - d.startX;
    const dy = e.clientY - d.startY;
    if (!d.moved && Math.hypot(dx, dy) > 5) d.moved = true; // a real drag, not a click
    if (d.moved) setPos(clampPos({ left: d.left + dx, top: d.top + dy }));
  };
  const onFabPointerUp = (e) => {
    const d = dragRef.current;
    d.active = false;
    e.currentTarget.releasePointerCapture?.(e.pointerId);
    if (d.moved) {
      try { localStorage.setItem(POS_KEY, JSON.stringify(pos)); } catch (err) { /* not saved */ }
    }
  };
  const onFabClick = () => {
    if (dragRef.current.moved) { dragRef.current.moved = false; return; } // end of a drag
    setOpen((o) => !o);
  };

  // Chat window sits next to the button: above it when there is room, else below; right edges
  // line up when the button is on the right half of the screen, left edges otherwise.
  const small = vp.w < 600;
  const winW = small ? vp.w - 32 : 420;
  const winH = Math.max(280, Math.min(small ? vp.h * 0.75 : 580, vp.h - 108));
  const winLeft = small
    ? 16
    : Math.max(EDGE, Math.min(vp.w - winW - EDGE, pos.left + FAB_SIZE / 2 > vp.w / 2 ? pos.left + FAB_SIZE - winW : pos.left));
  const roomAbove = pos.top - winH - 12 >= EDGE;
  const winTop = roomAbove
    ? pos.top - winH - 12
    : Math.max(EDGE, Math.min(vp.h - winH - EDGE, pos.top + FAB_SIZE + 12));
  const [messages, setMessages] = useState(() => [makeWelcome()]);
  const [input, setInput] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [loadingSugg, setLoadingSugg] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [lastMember, setLastMember] = useState(null);
  const endRef = useRef(null);
  const debRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages, fetching]);

  const fetchSuggestions = useCallback((val) => {
    clearTimeout(debRef.current);
    if (!val || val.length < 2) { setSuggestions([]); return; }
    debRef.current = setTimeout(async () => {
      const auth = getAuth(); if (!auth) return;
      setLoadingSugg(true);
      try {
        const r = await AutocompleteMember(auth.token, auth.userId, val);
        const base = Array.isArray(r?.data)
          ? r.data.slice(0, 8).map(m => (typeof m === 'string' ? m : m.search_data || m.name || ''))
          : [];
        const extras = [
          "Today's income", "This month's income", 'Gym summary',
          'Expiring soon members', 'Due members', 'Active PT members', 'Monthly revenue trend',
          ...(lastMember ? [`Payment history for ${lastMember.name}`] : []),
        ].filter(e => e.toLowerCase().includes(val.toLowerCase()));
        setSuggestions([...base, ...extras]);
      } catch { setSuggestions([]); }
      finally { setLoadingSugg(false); }
    }, 320);
  }, [lastMember]);

  const addMsg = (msg) => setMessages(p => [...p, msg]);

  const dispatch = useCallback(async (rawQuery) => {
    const query = rawQuery.trim();
    if (!query || fetching) return;
    setInput(''); setSuggestions([]);

    addMsg({ type: 'user', text: query, id: Date.now() });

    const auth = getAuth();
    if (!auth) { addMsg({ type: 'bot', stream: true, text: 'Please log in first.', id: Date.now() }); return; }

    const intent = detectIntent(query);

    // ── income ──
    if (intent === 'income_today' || intent === 'income_month') {
      setFetching(true);
      try {
        const d = await DashboardCount(auth.token, auth.userId);
        addMsg({ type: 'bot', stream: true, text: intent === 'income_today' ? "Here's today's income summary:" : "Here's this month's income summary:", id: Date.now() });
        addMsg({ type: 'income', incomeType: intent, data: d, id: Date.now() + 1 });
      } catch { addMsg({ type: 'bot', stream: true, text: 'Could not fetch income data.', id: Date.now() }); }
      finally { setFetching(false); }
      return;
    }

    // ── gym summary ──
    if (intent === 'gym_summary') {
      setFetching(true);
      try {
        const d = await DashboardCount(auth.token, auth.userId);
        addMsg({ type: 'bot', stream: true, text: "Here's your gym analytics overview:", id: Date.now() });
        addMsg({ type: 'gym_summary', data: d, id: Date.now() + 1 });
      } catch { addMsg({ type: 'bot', stream: true, text: 'Could not fetch gym summary.', id: Date.now() }); }
      finally { setFetching(false); }
      return;
    }

    // ── expiring soon ──
    if (intent === 'expiring_soon') {
      setFetching(true);
      try {
        const r = await getUpcomingExpire(auth.token, 30, '');
        const members = Array.isArray(r?.data) ? r.data : Array.isArray(r) ? r : [];
        addMsg({
          type: 'bot', stream: true,
          text: members.length
            ? `Found ${members.length} member${members.length !== 1 ? 's' : ''} with upcoming membership expiry:`
            : "No members with upcoming expiry found.",
          id: Date.now(),
        });
        if (members.length) addMsg({ type: 'expiring_soon', members, id: Date.now() + 1 });
      } catch { addMsg({ type: 'bot', stream: true, text: 'Could not fetch expiring members.', id: Date.now() }); }
      finally { setFetching(false); }
      return;
    }

    // ── due members ──
    if (intent === 'due_members') {
      setFetching(true);
      try {
        const r = await getPendingDue(auth.token, 30, '');
        const members = Array.isArray(r?.data) ? r.data : Array.isArray(r) ? r : [];
        addMsg({
          type: 'bot', stream: true,
          text: members.length
            ? `Found ${members.length} member${members.length !== 1 ? 's' : ''} with pending dues:`
            : "No pending dues found. All clear! ✅",
          id: Date.now(),
        });
        if (members.length) addMsg({ type: 'due_members', members, id: Date.now() + 1 });
      } catch { addMsg({ type: 'bot', stream: true, text: 'Could not fetch due members.', id: Date.now() }); }
      finally { setFetching(false); }
      return;
    }

    // ── active PT ──
    if (intent === 'active_pt') {
      setFetching(true);
      try {
        const r = await getActivePT(auth.token, 30, '');
        const members = Array.isArray(r?.data) ? r.data : Array.isArray(r) ? r : [];
        addMsg({
          type: 'bot', stream: true,
          text: members.length
            ? `Found ${members.length} active PT member${members.length !== 1 ? 's' : ''}:`
            : "No active PT members found.",
          id: Date.now(),
        });
        if (members.length) addMsg({ type: 'active_pt', members, id: Date.now() + 1 });
      } catch { addMsg({ type: 'bot', stream: true, text: 'Could not fetch active PT members.', id: Date.now() }); }
      finally { setFetching(false); }
      return;
    }

    // ── monthly trend ──
    if (intent === 'monthly_trend') {
      setFetching(true);
      try {
        const r = await DashboardBarChart(auth.token, auth.userId);
        const data = r?.data || r?.monthly || (Array.isArray(r) ? r : []);
        addMsg({ type: 'bot', stream: true, text: "Here's your monthly revenue trend:", id: Date.now() });
        addMsg({ type: 'monthly_trend', data, id: Date.now() + 1 });
      } catch { addMsg({ type: 'bot', stream: true, text: 'Could not fetch monthly trend.', id: Date.now() }); }
      finally { setFetching(false); }
      return;
    }

    // ── payment history ──
    if (intent === 'payment_history') {
      const target = lastMember;
      if (!target) {
        addMsg({ type: 'bot', stream: true, text: "Please search for a member first, then ask for their payment history.", id: Date.now() });
        return;
      }
      setFetching(true);
      try {
        const r = await retrievePaymentHistory(auth.token, auth.userId, target.id, '', 20, 0, '', '', '');
        const payments = r?.payments ?? r?.data ?? [];
        addMsg({ type: 'bot', stream: true, text: `Showing payment history for ${target.name}:`, id: Date.now() });
        addMsg({ type: 'payment_history', memberName: target.name, payments: Array.isArray(payments) ? payments : [], id: Date.now() + 1 });
      } catch { addMsg({ type: 'bot', stream: true, text: 'Could not fetch payment history.', id: Date.now() }); }
      finally { setFetching(false); }
      return;
    }

    // ── member search ──
    const nameOnly = query.split('|')[0].trim();
    setFetching(true);
    try {
      const r = await retrieveGymMember(auth.token, auth.userId, nameOnly, 10, 0);
      const members = r?.code == '200' && Array.isArray(r.data) ? r.data.slice(0, 10) : [];
      if (members.length === 0) {
        addMsg({ type: 'bot', stream: true, text: `No members found for "${nameOnly}". Try a different name or use a quick action below.`, id: Date.now() });
        return;
      }

      const enriched = await Promise.all(members.map(async (m) => {
        try {
          const ph = await retrievePaymentHistory(auth.token, auth.userId, m.id, '', 1, 0, '', '', '');
          const list = ph?.payments ?? ph?.data;
          return { ...m, lastPayment: Array.isArray(list) && list.length ? list[0] : null };
        } catch { return { ...m, lastPayment: null }; }
      }));

      setLastMember(enriched[0]);
      addMsg({
        type: 'bot', stream: true,
        text: enriched.length === 1 ? `Found 1 member matching "${nameOnly}":` : `Found ${enriched.length} members matching "${nameOnly}":`,
        id: Date.now(),
      });
      addMsg({ type: 'members', members: enriched, id: Date.now() + 1 });
    } catch { addMsg({ type: 'bot', stream: true, text: 'Something went wrong. Please try again.', id: Date.now() }); }
    finally { setFetching(false); }
  }, [fetching, lastMember]);

  const handleFollowUp = useCallback(async (action, member) => {
    if (action === 'payment_history' && member) {
      const auth = getAuth(); if (!auth) return;
      addMsg({ type: 'user', text: `Payment history for ${member.name}`, id: Date.now() });
      setFetching(true);
      try {
        const r = await retrievePaymentHistory(auth.token, auth.userId, member.id, '', 20, 0, '', '', '');
        const payments = r?.payments ?? r?.data ?? [];
        addMsg({ type: 'bot', stream: true, text: `Here are the payment records for ${member.name}:`, id: Date.now() });
        addMsg({ type: 'payment_history', memberName: member.name, payments: Array.isArray(payments) ? payments : [], id: Date.now() + 1 });
      } catch { addMsg({ type: 'bot', stream: true, text: 'Could not fetch payment history.', id: Date.now() }); }
      finally { setFetching(false); }
      return;
    }
    if (action in QUICK_ACTION_MAP) dispatch(QUICK_ACTION_MAP[action]);
  }, [dispatch]);

  const handleSelect = (_, val) => { if (val) dispatch(val); };
  const handleKeyDown = (e) => { if (e.key === 'Enter' && input.trim() && !loadingSugg) dispatch(input); };

  return (
    <>
      <Zoom in>
        <Fab
          onClick={onFabClick}
          onPointerDown={onFabPointerDown}
          onPointerMove={onFabPointerMove}
          onPointerUp={onFabPointerUp}
          onPointerCancel={onFabPointerUp}
          aria-label="Gym assistant (drag to move)"
          title="Gym assistant — drag to move"
          sx={{
            position: 'fixed', left: pos.left, top: pos.top, zIndex: 1300,
            touchAction: 'none', cursor: 'grab', '&:active': { cursor: 'grabbing' },
            background: `linear-gradient(135deg,${appColors.evergreen},${appColors.leaf})`,
            color: 'white', width: FAB_SIZE, height: FAB_SIZE,
            boxShadow: '0 6px 24px rgba(21,101,192,0.45)',
            '&:hover': { background: `linear-gradient(135deg,${appColors.evergreen},${appColors.leaf})` },
          }}
        >
          {open ? <CloseIcon /> : <SmartToyIcon />}
        </Fab>
      </Zoom>

      <Zoom in={open} unmountOnExit>
        <Paper elevation={0} sx={{
          position: 'fixed', left: winLeft, top: winTop, zIndex: 1299,
          width: winW,
          height: winH,
          display: 'flex', flexDirection: 'column', borderRadius: 3,
          overflow: 'hidden',
          border: '1px solid rgba(0,0,0,0.08)',
          boxShadow: '0 20px 60px rgba(0,0,0,0.18)',
        }}>

          {/* Header */}
          <Box sx={{
            background: `linear-gradient(135deg,${appColors.evergreen} 0%,${appColors.leaf} 100%)`,
            color: 'white', px: 2, py: 1.5,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0,
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
              <Avatar sx={{ width: 34, height: 34, bgcolor: 'rgba(255,255,255,0.15)', border: '2px solid rgba(255,255,255,0.3)' }}>
                <SmartToyIcon sx={{ fontSize: 18 }} />
              </Avatar>
              <Box>
                <Typography fontWeight={700} fontSize={14} lineHeight={1.2}>Gym Assistant</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
                  <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#4ade80', animation: 'pulse 2s infinite', '@keyframes pulse': { '0%,100%': { opacity: 1 }, '50%': { opacity: 0.5 } } }} />
                  <Typography fontSize={10} sx={{ opacity: 0.85 }}>Online · AI powered</Typography>
                </Box>
              </Box>
            </Box>
            <Box>
              <Tooltip title="Clear chat">
                <IconButton size="small" sx={{ color: 'rgba(255,255,255,0.8)', '&:hover': { color: 'white' } }}
                  onClick={() => { setMessages([makeWelcome()]); setLastMember(null); }}>
                  <DeleteSweepIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <IconButton size="small" sx={{ color: 'rgba(255,255,255,0.8)', '&:hover': { color: 'white' } }} onClick={() => setOpen(false)}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>

          {/* Messages */}
          <Box sx={{ flex: 1, overflowY: 'auto', p: 1.5, bgcolor: '#f8fafc' }}>
            {messages.map(msg => (
              <Box key={msg.id}>
                {msg.type === 'user' && <UserBubble text={msg.text} />}

                {msg.type === 'bot' && (
                  <BotBubble
                    text={msg.text}
                    stream={msg.stream}
                    extra={msg.showQuickChips
                      ? <QuickChips onAction={a => dispatch(QUICK_ACTION_MAP[a] || a)} />
                      : null}
                  />
                )}

                {msg.type === 'members' && (
                  <BotBubble noPad>
                    {msg.members.map((m, i) => <MemberCard key={i} member={m} onFollowUp={handleFollowUp} />)}
                  </BotBubble>
                )}

                {msg.type === 'payment_history' && (
                  <BotBubble noPad>
                    <PaymentHistoryCard memberName={msg.memberName} payments={msg.payments} />
                  </BotBubble>
                )}

                {msg.type === 'income' && (
                  <BotBubble noPad>
                    <IncomeCard type={msg.incomeType} data={msg.data} />
                  </BotBubble>
                )}

                {msg.type === 'gym_summary' && (
                  <BotBubble noPad>
                    <GymSummaryCard data={msg.data} />
                  </BotBubble>
                )}

                {msg.type === 'expiring_soon' && (
                  <BotBubble noPad>
                    <ExpiringMembersCard members={msg.members} />
                  </BotBubble>
                )}

                {msg.type === 'due_members' && (
                  <BotBubble noPad>
                    <DueMembersCard members={msg.members} />
                  </BotBubble>
                )}

                {msg.type === 'active_pt' && (
                  <BotBubble noPad>
                    <ActivePTCard members={msg.members} />
                  </BotBubble>
                )}

                {msg.type === 'monthly_trend' && (
                  <BotBubble noPad>
                    <MonthlyTrendCard data={msg.data} />
                  </BotBubble>
                )}
              </Box>
            ))}

            {fetching && <BotBubble text="" extra={<TypingDots />} />}
            <div ref={endRef} />
          </Box>

          <Divider />

          {/* Persistent quick actions — always visible */}
          <Box sx={{
            px: 1.25, pt: 0.9, pb: 0.5, bgcolor: 'background.paper', flexShrink: 0,
            borderBottom: '1px solid', borderColor: 'divider',
          }}>
            <Typography variant="caption" color="text.disabled" sx={{ fontSize: 9, letterSpacing: 0.4, textTransform: 'uppercase', display: 'block', mb: 0.5 }}>
              Quick Actions
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.6, overflowX: 'auto', pb: 0.5, '&::-webkit-scrollbar': { height: 3 }, '&::-webkit-scrollbar-thumb': { bgcolor: '#cbd5e1', borderRadius: 2 } }}>
              {[
                { label: "Today's Income", icon: <TodayIcon />, action: 'income_today', color: 'success' },
                { label: 'This Month', icon: <DateRangeIcon />, action: 'income_month', color: 'primary' },
                { label: 'Gym Summary', icon: <AssessmentIcon />, action: 'gym_summary', color: 'secondary' },
                { label: 'Expiring Soon', icon: <WarningAmberIcon />, action: 'expiring_soon', color: 'warning' },
                { label: 'Due Members', icon: <PaymentsIcon />, action: 'due_members', color: 'error' },
                { label: 'Active PT', icon: <FitnessCenterIcon />, action: 'active_pt', color: 'success' },
                { label: 'Monthly Trend', icon: <TrendingUpIcon />, action: 'monthly_trend', color: 'primary' },
              ].map(c => (
                <Chip key={c.action} icon={c.icon} label={c.label} size="small" color={c.color}
                  variant="outlined" onClick={() => dispatch(QUICK_ACTION_MAP[c.action] || c.action)}
                  disabled={fetching}
                  sx={{ fontSize: 10, height: 24, cursor: 'pointer', flexShrink: 0 }}
                />
              ))}
            </Box>
          </Box>

          {/* Input */}
          <Box sx={{ p: 1.25, bgcolor: 'background.paper', flexShrink: 0 }}>
            <Autocomplete
              freeSolo options={suggestions} inputValue={input}
              onInputChange={(_, v) => { setInput(v); fetchSuggestions(v); }}
              onChange={handleSelect} loading={loadingSugg}
              filterOptions={x => x} disableClearable
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Ask me anything…"
                  size="small"
                  onKeyDown={handleKeyDown}
                  disabled={fetching}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3, fontSize: 13 } }}
                  slotProps={{
                    input: {
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingSugg && <CircularProgress size={13} />}
                          {input && (
                            <IconButton
                              size="small"
                              onClick={() => { setInput(''); setSuggestions([]); }}
                              sx={{ p: 0.3, color: 'text.disabled', '&:hover': { color: 'text.secondary' } }}
                            >
                              <CloseIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                          )}
                          <IconButton
                            size="small" color="primary"
                            onClick={() => input.trim() && dispatch(input)}
                            disabled={!input.trim() || fetching}
                            sx={{ bgcolor: input.trim() ? 'primary.main' : 'transparent', color: input.trim() ? 'white' : 'text.disabled', borderRadius: 2, p: 0.5, '&:hover': { bgcolor: 'primary.dark' } }}
                          >
                            <SendIcon sx={{ fontSize: 15 }} />
                          </IconButton>
                        </>
                      ),
                    },
                  }}
                />
              )}
            />
            <Typography variant="caption" color="text.disabled" sx={{ mt: 0.4, display: 'block', textAlign: 'center', fontSize: 10 }}>
              Type a member name or use quick actions above
            </Typography>
          </Box>
        </Paper>
      </Zoom>
    </>
  );
}
